import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, SearchLg, Eye, Trash01 } from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Dialog, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  getInvoices,
  deleteInvoice,
  type InvoiceWithRelations,
} from "@/api/invoices";
import type { PaginatedResponse } from "@/types/dto";

const CAN_CREATE = ["admin", "owner", "manager"];
const CAN_DELETE = ["admin", "owner"];

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  paid: "bg-green-100 text-green-700",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[status] ?? "bg-gray-100 text-gray-700"}`}
    >
      {status}
    </span>
  );
}

export default function InvoicesListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [result, setResult] = useState<PaginatedResponse<InvoiceWithRelations> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const canCreate = CAN_CREATE.includes(user?.role ?? "");
  const canDelete = CAN_DELETE.includes(user?.role ?? "");

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getInvoices({ page, limit: 20, search: search || undefined });
      setResult(data);
    } catch {
      // handled
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    setSaving(true);
    try {
      await deleteInvoice(deleteConfirmId);
      setDeleteConfirmId(null);
      fetchInvoices();
      toast.success("Invoice deleted");
    } catch {
      toast.error("Failed to delete invoice");
    } finally {
      setSaving(false);
    }
  };

  const invoices = result?.data ?? [];
  const pagination = result?.pagination;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  return (
    <PageLayout
      title="Invoices"
      subtitle="Manage billing and payments"
      actions={
        canCreate ? (
          <Button
            size="md"
            color="primary"
            iconLeading={Plus}
            onClick={() => navigate("/invoices/new")}
          >
            New Invoice
          </Button>
        ) : undefined
      }
    >
      <div className="mb-4 sm:mb-6 max-w-sm">
        <Input
          placeholder="Search by invoice number..."
          icon={SearchLg}
          value={search}
          onChange={(v: string) => { setSearch(v); setPage(1); }}
        />
      </div>

      {loading && invoices.length === 0 ? (
        <div className="py-12 text-center text-text-tertiary">Loading...</div>
      ) : invoices.length === 0 ? (
        <div className="py-12 text-center text-text-tertiary">
          {search ? "No invoices match your search." : "No invoices yet."}
        </div>
      ) : (
        <>
          {/* Mobile Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 xl:hidden">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="rounded-xl border border-border-secondary bg-bg-primary p-4"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-text-primary">
                        #{inv.invoiceNumber}
                      </p>
                      <StatusBadge status={inv.status} />
                    </div>
                    <p className="text-sm text-text-secondary mt-0.5">
                      {inv.customer?.firstName} {inv.customer?.lastName}
                    </p>
                  </div>
                  <p className="font-semibold text-text-primary shrink-0 ml-2">
                    {formatCurrency(inv.total)}
                  </p>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-text-tertiary">
                    Due: {inv.dueDate}
                  </p>
                  {canDelete && (
                    <Button
                      size="sm"
                      color="link-destructive"
                      iconLeading={Trash01}
                      onClick={() => setDeleteConfirmId(inv.id)}
                      aria-label="Delete"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden xl:block overflow-x-auto rounded-xl border border-border-secondary">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-secondary bg-bg-secondary">
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Invoice #</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Customer</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Status</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Due Date</th>
                  <th className="px-6 py-3 text-right font-medium text-text-tertiary">Total</th>
                  <th className="px-6 py-3 text-right font-medium text-text-tertiary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-border-secondary last:border-0 hover:bg-bg-secondary_hover transition-colors"
                  >
                    <td className="px-6 py-4 font-medium text-text-primary">#{inv.invoiceNumber}</td>
                    <td className="px-6 py-4 text-text-secondary">
                      {inv.customer
                        ? `${inv.customer.firstName} ${inv.customer.lastName}`
                        : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="px-6 py-4 text-text-secondary">{inv.dueDate}</td>
                    <td className="px-6 py-4 text-right font-medium text-text-primary">
                      {formatCurrency(inv.total)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          color="link-gray"
                          iconLeading={Eye}
                          onClick={() => navigate(`/invoices/${inv.id}`)}
                          aria-label="View"
                        />
                        {canDelete && (
                          <Button
                            size="sm"
                            color="link-destructive"
                            iconLeading={Trash01}
                            onClick={() => setDeleteConfirmId(inv.id)}
                            aria-label="Delete"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-tertiary">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <Button size="sm" color="secondary" isDisabled={pagination.page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button size="sm" color="secondary" isDisabled={pagination.page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogDescription>Are you sure? This action cannot be undone.</DialogDescription>
        <div className="flex justify-end gap-3">
          <Button size="md" color="secondary" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button size="md" color="primary-destructive" isLoading={saving} isDisabled={saving} onClick={handleDelete}>Delete</Button>
        </div>
      </Dialog>
    </PageLayout>
  );
}
